import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FiHash, FiCheckCircle, FiClock, FiAlertCircle,
  FiUser, FiMail, FiChevronDown, FiChevronUp, FiRefreshCw,
} from 'react-icons/fi';
import { getSupportTickets, resolveTicket, updateTicketStatus } from '../../services/api';
import styles from './SupportTicketsPage.module.css';

const TABS = [
  { id: 'open',        label: 'Open' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'resolved',    label: 'Resolved' },
  { id: 'all',         label: 'All' },
];

const STATUS_CONFIG = {
  open:        { label: 'Open',        icon: FiAlertCircle, color: '#2563eb', bg: '#eff6ff' },
  in_progress: { label: 'In Progress', icon: FiClock,       color: '#d97706', bg: '#fffbeb' },
  resolved:    { label: 'Resolved',    icon: FiCheckCircle, color: '#16a34a', bg: '#f0fdf4' },
};

const fmt = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const TicketRow = ({ ticket, onRefresh }) => {
  const [expanded, setExpanded]       = useState(false);
  const [resolution, setResolution]   = useState(ticket.resolution || '');
  const [resolving, setResolving]     = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const cfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const StatusIcon = cfg.icon;

  const handleResolve = async () => {
    if (!resolution.trim()) { toast.error('Please enter a resolution note'); return; }
    setResolving(true);
    try {
      await resolveTicket(ticket.id, { resolution, status: 'resolved' });
      toast.success(`Ticket ${ticket.ticketNo} marked as resolved`);
      onRefresh();
    } catch {
      toast.error('Failed to resolve ticket');
    } finally {
      setResolving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === ticket.status) return;
    setStatusLoading(true);
    try {
      await updateTicketStatus(ticket.id, newStatus);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      onRefresh();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <div className={styles.ticketCard}>
      {/* Header row */}
      <div className={styles.ticketHeader} onClick={() => setExpanded(e => !e)}>
        <div className={styles.ticketLeft}>
          <span className={styles.ticketNo}><FiHash size={12} />{ticket.ticketNo}</span>
          <div className={styles.ticketSubject}>{ticket.subject}</div>
          <div className={styles.ticketMeta}>
            <span><FiUser size={12} />{ticket.name}</span>
            <span><FiMail size={12} />{ticket.email}</span>
            <span><FiClock size={12} />{fmt(ticket.createdAt)}</span>
          </div>
        </div>
        <div className={styles.ticketRight}>
          <span className={styles.statusPill} style={{ background: cfg.bg, color: cfg.color }}>
            <StatusIcon size={12} />{cfg.label}
          </span>
          {expanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className={styles.ticketBody}>
          <div className={styles.messageBox}>
            <div className={styles.messageLabel}>Message</div>
            <p>{ticket.message}</p>
          </div>

          {ticket.resolution && (
            <div className={styles.resolutionBox}>
              <div className={styles.resolutionLabel}>Resolution</div>
              <p>{ticket.resolution}</p>
              {ticket.resolvedAt && <div className={styles.resolvedAt}>Resolved on {fmt(ticket.resolvedAt)}</div>}
            </div>
          )}

          <div className={styles.actions}>
            {/* Quick status change */}
            <div className={styles.statusSelect}>
              <label className={styles.actionLabel}>Change Status</label>
              <div className={styles.statusButtons}>
                {Object.entries(STATUS_CONFIG).map(([key, s]) => (
                  <button
                    key={key}
                    className={`${styles.statusBtn} ${ticket.status === key ? styles.statusBtnActive : ''}`}
                    style={ticket.status === key ? { background: s.bg, color: s.color, borderColor: s.color } : {}}
                    onClick={() => handleStatusChange(key)}
                    disabled={statusLoading || ticket.status === key}
                  >
                    <s.icon size={12} /> {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Resolution note */}
            {ticket.status !== 'resolved' && (
              <div className={styles.resolveSection}>
                <label className={styles.actionLabel}>Resolution Note (required to close)</label>
                <textarea
                  className={styles.resolutionInput}
                  value={resolution}
                  onChange={e => setResolution(e.target.value)}
                  placeholder="Describe what was done to resolve this issue…"
                  rows={3}
                />
                <button
                  className={styles.btnResolve}
                  onClick={handleResolve}
                  disabled={resolving}
                >
                  <FiCheckCircle size={14} />
                  {resolving ? 'Resolving…' : 'Mark as Resolved'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SupportTicketsPage = () => {
  const [tab, setTab]         = useState('open');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSupportTickets(tab);
      setTickets(res.data);
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const counts = {
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Support Tickets</h1>
          <p className={styles.subtitle}>Review and resolve user support requests</p>
        </div>
        <button className={styles.btnRefresh} onClick={fetchTickets} disabled={loading}>
          <FiRefreshCw size={15} className={loading ? styles.spinning : ''} />
          Refresh
        </button>
      </div>

      {/* Summary badges */}
      <div className={styles.summaryRow}>
        <div className={styles.summaryCard} style={{ borderColor: '#2563eb' }}>
          <FiAlertCircle size={20} style={{ color: '#2563eb' }} />
          <div>
            <div className={styles.summaryCount} style={{ color: '#2563eb' }}>{counts.open}</div>
            <div className={styles.summaryLabel}>Open</div>
          </div>
        </div>
        <div className={styles.summaryCard} style={{ borderColor: '#d97706' }}>
          <FiClock size={20} style={{ color: '#d97706' }} />
          <div>
            <div className={styles.summaryCount} style={{ color: '#d97706' }}>{counts.in_progress}</div>
            <div className={styles.summaryLabel}>In Progress</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className={styles.loader}><div className={styles.spinner} /></div>
      ) : tickets.length === 0 ? (
        <div className={styles.empty}>
          <FiCheckCircle size={36} />
          <p>No {tab === 'all' ? '' : tab.replace('_', ' ')} tickets</p>
        </div>
      ) : (
        <div className={styles.ticketList}>
          {tickets.map(t => (
            <TicketRow key={t.id} ticket={t} onRefresh={fetchTickets} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SupportTicketsPage;
